import type { CustomerConfigurationResponse } from '@/app/api/customers/[customerId]/configuration/route'
import type { NetworkStandardLiveResponse } from '@/app/api/customers/[customerId]/network-standard/live/route'
import type { NetworkStandardPlanResponse } from '@/app/api/customers/[customerId]/network-standard/plan/route'
import type { NetworkStandardPushResponse } from '@/app/api/customers/[customerId]/network-standard/push/route'
import type { NetworkStandardRestorePlanResponse } from '@/app/api/customers/[customerId]/network-standard/restore-plan/route'
import type { NetworkStandardResponse } from '@/app/api/customers/[customerId]/network-standard/route'
import type { NetworkStandardSnapshotDeleteResponse } from '@/app/api/customers/[customerId]/network-standard/snapshots/delete/route'
import type {
  NetworkStandardSnapshotResponse,
  NetworkStandardSnapshotsResponse
} from '@/app/api/customers/[customerId]/network-standard/snapshots/route'
import type { NetworkStandardVerifyResponse } from '@/app/api/customers/[customerId]/network-standard/verify/route'
import {
  CustomerContractsResponse,
  ServicesSoldResponse,
  CustomerNavAttributesResponse
} from '@/features/internal/account/contracts/contractsTypes'
import type { ChannelType } from '@/features/internal/channels/channelTypes'
import { type SearchValues } from '@/features/internal/customers/components/customer-search-form'
import {
  CustomerType,
  TechnicianType,
  CustomerManagedNetworkResponse,
  NetworkBuildResponse,
  NetworkBuild,
  TechSkill,
  TechEquipment,
  TechSkillType,
  TechEquipmentType
} from '@/features/internal/customers/customerTypes'
import { SpecialNoteType } from '@/features/internal/customers/customerTypes'
import type { TimelineItem } from '@/features/internal/customers/customerTypes'
import type { CustomerFeaturesResponse } from '@/features/internal/hub/types'
import type {
  SmartboxJobAction,
  SmartboxJobListItem,
  SmartboxJobRun,
  SmartboxJobStart,
  SmartboxSummary
} from '@/features/internal/smartboxes/smartboxTypes'
import {
  type PortfolioPropertyType,
  type PropertyType
} from '@/features/portal/propertyTypes'
import type { FedExTrackingType } from '@/lib/fedex.types'
import type { EditableStep } from '@/lib/network-standards/plan-edits'
import type { PrestageSiteInputs } from '@/lib/network-standards/prestage-types'
import type { SoldService } from '@/lib/states/services-sold'
import type { CustomerEntityTarget } from '@/server/customers/entity-customer-scope'
import type { PropertyImportResolution } from '@/server/customers/property-import-rows'
import type { ServiceMuteSummary } from '@/server/smartbox-mute/summary'
import { baseApi } from '@/stores/api/base-api'

export interface SurveyListItem {
  sysId: string
  number: string
  metricType?: string
  surveyName: string | null
  state: string
  takenOn: string | null
  percentAnswered: string | null
  customerNumber?: string | null
  customerName?: string | null
  /** Raw BC cs_manager code (e.g. MILTON) — fallback when name is unresolved */
  csManager?: string | null
  /** Friendly CSM name resolved server-side via the BC user-code map */
  csManagerName?: string | null
  techId?: string | null
  techName?: string | null
  surveyType?: 'support' | 'technician'
  contactEmail?: string | null
  /** Case the survey was generated from (CSAT: trigger case; tech: WO's case) */
  caseNumber?: string | null
  /** Work order the survey came from, when one exists */
  workOrderNumber?: string | null
  averageScore?: number | null
  questionScores?: { name: string; score: number }[]
}

interface SurveyQuestion {
  sysId: string
  instance: string
  metric: string
  value: string | null
  stringValue: string | null
  questionText: string | null
  questionName: string | null
  order: string | null
  datatype: string | null
}

export interface CsatQuestion {
  name: string
  averageScore: number | null
  responseCount: number
}

export interface CsatScoreData {
  responseCount: number
  questions: CsatQuestion[]
}

interface ResolvePropertyImportRequest {
  /** Omitted while onboarding: the org doesn't exist yet. */
  target?: CustomerEntityTarget
  file: File
  /** Ids already picked in the dialog, reported as already selected. */
  selectedIds: string[]
  /** Ids the dialog already treats as assigned (e.g. a member's group ones). */
  assignedIds: string[]
}

interface AssignCustomersResult {
  success: boolean
  /** Ids skipped because a group or member can't take outside the org pool. */
  notInOrg?: string[]
}

export const customerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // 🔍 Basic search
    searchCustomers: builder.query<
      CustomerType[],
      { query: string; organizationId: string }
    >({
      query: ({ query, organizationId }) => ({
        url: `/customers/search`,
        params: { q: query, organizationId }
      }),
      providesTags: ['Customers']
    }),

    // Slim typeahead — 6 fields per row vs full row from /search. Used by
    // assignment pickers (org wizard, entity customers, etc).
    searchCustomersTypeahead: builder.query<
      Array<{
        id: string
        name: string
        city: string | null
        state: string | null
        country: string | null
        theirNumber: string | null
      }>,
      { q: string; organizationId?: string }
    >({
      query: ({ q, organizationId }) => ({
        url: '/customers/search/typeahead',
        params: organizationId ? { q, organizationId } : { q }
      }),
      providesTags: ['Customers']
    }),

    // Bulk id → slim-row resolution (CSV import, bulk pickers). POST because
    // the id list can exceed query-string limits; one-shot, so a mutation.
    lookupCustomersByIds: builder.mutation<
      Array<{
        id: string
        name: string
        city: string | null
        state: string | null
        country: string | null
        theirNumber: string | null
      }>,
      { ids: string[]; organizationId?: string }
    >({
      query: (body) => ({
        url: '/customers/search/by-ids',
        method: 'POST',
        body
      })
    }),

    // 🔎 Advanced search
    searchCustomersAdvanced: builder.query<
      CustomerType[],
      SearchValues & { organizationId: string }
    >({
      query: (rawParams) => {
        // Arrays go as repeated keys: service names are BC free text, so a
        // comma-joined list could split one name in two.
        const params = new URLSearchParams({ include: 'servicesSold' })
        for (const [key, value] of Object.entries(rawParams)) {
          if (value == null || value === '') continue
          if (Array.isArray(value)) {
            for (const v of value) params.append(key, v)
          } else {
            params.set(key, String(value))
          }
        }

        return { url: `/customers/search/advanced?${params}` }
      },
      providesTags: ['Customers']
    }),
    getEffectiveCustomersForUser: builder.query<
      { customers: CustomerType[] },
      { userId: string; organizationId?: string }
    >({
      query: ({ userId, organizationId }) => ({
        url: `/users/${userId}/customers`,
        params: organizationId ? { organizationId } : undefined
      }),
      // Assign/remove invalidate 'Customers'; without claiming the tag this
      // query never refetched, so the member Properties tab kept showing the
      // pre-assign list until a hard reload. That was unreachable while the
      // effective set included every org-pool row (the Add picker had nothing
      // left to offer) and became visible as soon as it didn't.
      providesTags: ['Customers']
    }),
    getEntityCustomers: builder.query<
      { customers: CustomerType[] },
      {
        entityType: 'user' | 'group' | 'organization'
        entityId: string
        organizationId: string
      }
    >({
      query: ({ entityType, entityId, organizationId }) => ({
        url: `/customers/entities`,
        method: 'GET',
        params: { entityType, entityId, organizationId }
      }),
      providesTags: ['Customers']
    }),
    getAccessibleCustomers: builder.query<
      { customers: CustomerType[] },
      { userId: string; slim?: boolean; includeServicesSold?: boolean }
    >({
      // `includeServicesSold` is slim-only — the full view never carries it.
      query: ({ slim, includeServicesSold }) =>
        slim
          ? `/customers/accessible?view=slim${includeServicesSold ? '&include=servicesSold' : ''}`
          : '/customers/accessible',
      providesTags: ['Customers']
    }),
    getCustomerAccess: builder.query<{ allowed: boolean }, string>({
      query: (customerId) =>
        `/customers/${encodeURIComponent(customerId)}/access`,
      providesTags: (_res, _err, customerId) => [
        { type: 'Customers', id: `access:${customerId}` }
      ]
    }),
    // `serviced` is deliberately not `active`: it counts every status where
    // the customer relationship is live (retention, seasonal, paused,
    // partially active, non-payment disconnect, renegotiating), not just
    // `status = 'ACTIVE'`. See SERVICED_CUSTOMER_STATUSES.
    getCustomerCounts: builder.query<
      { serviced: number; install: number; new: number },
      string | undefined
    >({
      query: (orgId) => {
        if (!orgId) return '/customers/counts'
        return `/customers/counts?orgId=${orgId}`
      },
      providesTags: ['Customers']
    }),
    assignCustomersToEntity: builder.mutation<
      AssignCustomersResult,
      {
        entityType: 'user' | 'group' | 'organization'
        entityId: string
        customerIds: string[]
        organizationId: string
      }
    >({
      query: ({ entityType, entityId, customerIds, organizationId }) => ({
        url: `/customers/entities`,
        method: 'POST',
        body: { entityType, entityId, customerIds, organizationId }
      }),
      invalidatesTags: ['Customers']
    }),
    // Checks a spreadsheet of customer numbers and returns the properties that
    // pass, for an Add properties dialog to select. Writes nothing. The target
    // rides in the query string: the body is the multipart file, which
    // withAuth's org-scope check cannot read.
    resolvePropertyImport: builder.mutation<
      PropertyImportResolution,
      ResolvePropertyImportRequest
    >({
      query: ({ target, file, selectedIds, assignedIds }) => {
        const form = new FormData()
        form.append('file', file)
        form.append('selected', JSON.stringify(selectedIds))
        form.append('assigned', JSON.stringify(assignedIds))
        return {
          url: '/customers/entities/import',
          method: 'POST',
          params: target ?? {},
          body: form,
          formData: true
        }
      }
    }),
    removeCustomersFromEntity: builder.mutation<
      void,
      {
        entityType: 'user' | 'group' | 'organization'
        entityId: string
        customerIds: string[]
        organizationId: string
      }
    >({
      query: ({ entityType, entityId, customerIds, organizationId }) => ({
        url: `/customers/entities`,
        method: 'DELETE',
        body: { entityType, entityId, customerIds, organizationId }
      }),
      invalidatesTags: ['Customers']
    }),

    // 📋 Get a single customer by ID (optional but useful)
    getCustomerById: builder.query<CustomerType, string>({
      query: (id) => `/customers/${id}`,
      providesTags: ['Customers']
    }),

    // 📃 Get all customers
    getAllCustomers: builder.query<CustomerType[], void>({
      query: () => '/customers',
      providesTags: ['Customers']
    }),
    getAllSpecialNotesByCustomerId: builder.query<SpecialNoteType[], string>({
      query: (customerId) => `/customers/${customerId}/special-notes`,
      providesTags: ['SpecialNotes']
    }),

    createSpecialNoteForCustomer: builder.mutation<
      void,
      { customerId: string; note: string }
    >({
      query: ({ customerId, note }) => ({
        url: `/customers/${customerId}/special-notes`,
        method: 'POST',
        body: { note }
      }),
      invalidatesTags: ['SpecialNotes']
    }),

    getAllSpecialNotesByTechnicianId: builder.query<SpecialNoteType[], string>({
      query: (technicianId) => `/technicians/${technicianId}/special-notes`,
      providesTags: ['SpecialNotes']
    }),

    createSpecialNoteForTechnician: builder.mutation<
      void,
      { technicianId: string; note: string }
    >({
      query: ({ technicianId, note }) => ({
        url: `/technicians/${technicianId}/special-notes`,
        method: 'POST',
        body: { note }
      }),
      invalidatesTags: ['SpecialNotes']
    }),

    getAllTechnicians: builder.query<TechnicianType[], void>({
      query: () => '/customers/technicians',
      providesTags: ['Technicians']
    }),
    getAllActiveTechnicians: builder.query<TechnicianType[], void>({
      query: () => '/customers/technicians/active',
      providesTags: ['Technicians']
    }),
    getTechnicianById: builder.query<
      TechnicianType,
      { id: string; code?: string }
    >({
      query: ({ id, code }) => {
        const url = `/customers/technicians/${id}`
        return code ? `${url}?code=${encodeURIComponent(code)}` : url
      },
      providesTags: ['Technicians']
    }),

    getTechnicianSkills: builder.query<
      TechSkill[],
      { techId: string; code: string }
    >({
      query: ({ techId, code }) => ({
        url: `/technicians/${techId}/skills`,
        params: { code }
      }),
      providesTags: ['TechnicianSkillsEquipment']
    }),
    getTechnicianEquipment: builder.query<
      TechEquipment[],
      { techId: string; code: string }
    >({
      query: ({ techId, code }) => ({
        url: `/technicians/${techId}/equipment`,
        params: { code }
      }),
      providesTags: ['TechnicianSkillsEquipment']
    }),
    getSkillTypes: builder.query<TechSkillType[], void>({
      query: () => '/technicians/skill-types',
      providesTags: ['TechSkillTypes']
    }),
    getTechEquipmentTypes: builder.query<TechEquipmentType[], void>({
      query: () => '/technicians/equipment-types',
      providesTags: ['TechEquipmentTypes']
    }),
    createTechnicianSkill: builder.mutation<
      { id: string },
      {
        techId: string
        code: string
        instance?: string
        skillTypeId: string
        notes?: string | null
      }
    >({
      query: ({ techId, ...body }) => ({
        url: `/technicians/${techId}/skills`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TechnicianSkillsEquipment', 'Technicians']
    }),
    updateTechnicianSkill: builder.mutation<
      { id: string },
      {
        techId: string
        skillId: string
        skillTypeId?: string
        notes?: string | null
      }
    >({
      query: ({ techId, skillId, ...body }) => ({
        url: `/technicians/${techId}/skills/${skillId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['TechnicianSkillsEquipment', 'Technicians']
    }),
    deleteTechnicianSkill: builder.mutation<
      { id: string },
      { techId: string; skillId: string }
    >({
      query: ({ techId, skillId }) => ({
        url: `/technicians/${techId}/skills/${skillId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['TechnicianSkillsEquipment', 'Technicians']
    }),
    createTechnicianEquipment: builder.mutation<
      { id: string },
      {
        techId: string
        code: string
        instance?: string
        equipmentTypeId: string
        equipmentDetails?: string | null
      }
    >({
      query: ({ techId, ...body }) => ({
        url: `/technicians/${techId}/equipment`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['TechnicianSkillsEquipment', 'Technicians']
    }),
    updateTechnicianEquipment: builder.mutation<
      { id: string },
      {
        techId: string
        equipmentId: string
        equipmentTypeId?: string
        equipmentDetails?: string | null
      }
    >({
      query: ({ techId, equipmentId, ...body }) => ({
        url: `/technicians/${techId}/equipment/${equipmentId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['TechnicianSkillsEquipment', 'Technicians']
    }),
    deleteTechnicianEquipment: builder.mutation<
      { id: string },
      { techId: string; equipmentId: string }
    >({
      query: ({ techId, equipmentId }) => ({
        url: `/technicians/${techId}/equipment/${equipmentId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['TechnicianSkillsEquipment', 'Technicians']
    }),

    getCustomerTimeline: builder.query<TimelineItem[], string>({
      query: (customerId) => ({
        url: `/customers/${customerId}/timeline`
      }),
      transformResponse: (response: {
        success: boolean
        data: TimelineItem[]
        count: number
      }) => response.data || [],
      providesTags: ['Timeline']
    }),

    // FedEx Tracking endpoints
    getFedExTracking: builder.query<
      FedExTrackingType[],
      { customerId: string; activeOnly?: boolean; last48Hours?: boolean }
    >({
      query: ({ customerId, activeOnly = false, last48Hours = false }) => ({
        url: `/customers/${customerId}/fedex-tracking`,
        params: {
          activeOnly: activeOnly.toString(),
          last48Hours: last48Hours.toString()
        }
      })
    }),

    getCustomerNavAttributes: builder.query<
      CustomerNavAttributesResponse,
      string
    >({
      query: (customerId) => `/customers/${customerId}/nav-attributes`
    }),
    getServicesSold: builder.query<ServicesSoldResponse, string>({
      query: (customerId) => `/customers/${customerId}/services-sold`
    }),
    // Every service sold somewhere — the Advanced Search picker's options.
    getSoldServiceOptions: builder.query<{ services: SoldService[] }, void>({
      query: () => '/customers/services-sold',
      keepUnusedDataFor: 10 * 60
    }),
    getCustomerContracts: builder.query<CustomerContractsResponse, string>({
      query: (customerId) => `/customers/${customerId}/contracts`
    }),
    // Get distinct countries from customers
    getCustomerCountries: builder.query<string[], string>({
      query: (organizationId) => ({
        url: '/customers/countries',
        params: { organizationId }
      }),
      providesTags: ['Customers']
    }),
    // Get customer local time and weather info
    getCustomerLocalInfo: builder.query<
      {
        timeZone: string
        weather: {
          temperature: number
          humidity: number
          windSpeed: number
          description: string
        } | null
      },
      { customerId: string; latitude: number; longitude: number }
    >({
      query: ({ customerId, latitude, longitude }) => ({
        url: `/customers/${customerId}/local-info`,
        params: {
          latitude: latitude.toString(),
          longitude: longitude.toString()
        }
      }),
      // Cache for 10 minutes
      keepUnusedDataFor: 600
    }),

    // GET: Fetch customer features aggregated from all sources (hub servers + database)
    getCustomerFeatures: builder.query<CustomerFeaturesResponse, string>({
      query: (customerId) => `/customers/${customerId}/features`,
      providesTags: (result, error, customerId) => [
        { type: 'CustomerFeatures', id: customerId }
      ],
      keepUnusedDataFor: 5 * 60 // 5 minutes
    }),

    // GET: Fetch customer managed network address from devices schema
    getCustomerManagedNetwork: builder.query<
      CustomerManagedNetworkResponse,
      string
    >({
      query: (customerId) => `/customers/${customerId}/managed-network`,
      providesTags: (result, error, customerId) => [
        { type: 'Customers', id: customerId }
      ],
      keepUnusedDataFor: 5 * 60 // 5 minutes
    }),

    // Which configuration sections a property is eligible for. Cached under
    // 'Customers' because every input is a property fact (brand, services sold,
    // BC status) rather than anything the configuration page itself writes.
    getCustomerConfiguration: builder.query<
      CustomerConfigurationResponse,
      string
    >({
      query: (customerId) => `/customers/${customerId}/configuration`,
      providesTags: (result, error, customerId) => [
        { type: 'Customers', id: `configuration:${customerId}` }
      ]
    }),

    // The brand network standard a property builds to, with its site facts and
    // the naming convention rendered for its own code. Tagged NetworkBuild
    // because the programme on the build sheet is one of its inputs: editing
    // the sheet can change which standard applies.
    getNetworkStandard: builder.query<NetworkStandardResponse, string>({
      query: (customerId) => `/customers/${customerId}/network-standard`,
      providesTags: (result, error, customerId) => [
        { type: 'NetworkBuild', id: customerId }
      ]
    }),

    // POST that writes nothing: the pre-stage plan for the operator's inputs.
    // A mutation rather than a lazy query so each "Build plan" click is a fresh
    // request with the current inputs and nothing is cached under a key.
    buildNetworkStandardPlan: builder.mutation<
      NetworkStandardPlanResponse,
      PrestageSiteInputs & {
        customerId: string
        networkId: string | null
      }
    >({
      query: ({ customerId, ...input }) => ({
        url: `/customers/${customerId}/network-standard/plan`,
        method: 'POST',
        body: input
      })
    }),

    // The live Meraki network scored against the standard. Fetched on demand
    // (lazy) and never kept: each click is a fresh read of the dashboard, and
    // a stale score shown as current is worse than none.
    getNetworkStandardLive: builder.query<
      NetworkStandardLiveResponse,
      { customerId: string; networkId: string | null }
    >({
      query: ({ customerId, networkId }) => ({
        url: `/customers/${customerId}/network-standard/live`,
        params: networkId ? { networkId } : undefined
      }),
      keepUnusedDataFor: 0
    }),

    // The network's stored snapshots with the tally each recorded. Carries
    // the same `Files:meraki_network-<id>` tag the files-api uses for this
    // context, so a save, a push or a files-area change refreshes it.
    getNetworkStandardSnapshots: builder.query<
      NetworkStandardSnapshotsResponse,
      { customerId: string; networkId: string }
    >({
      query: ({ customerId, networkId }) => ({
        url: `/customers/${customerId}/network-standard/snapshots`,
        params: { networkId }
      }),
      providesTags: (_result, _error, { networkId }) => [
        { type: 'Files', id: `meraki_network-${networkId}` }
      ]
    }),

    // Reads the live network and keeps it as a file under the network's
    // context, so the backup list (tag `Files:meraki_network-<id>`)
    // refreshes on success — both apis inject into baseApi, so the tag
    // registry is shared.
    saveNetworkStandardSnapshot: builder.mutation<
      NetworkStandardSnapshotResponse,
      { customerId: string; networkId: string }
    >({
      query: ({ customerId, networkId }) => ({
        url: `/customers/${customerId}/network-standard/snapshots`,
        method: 'POST',
        body: { networkId }
      }),
      invalidatesTags: (_result, _error, { networkId }) => [
        { type: 'Files', id: `meraki_network-${networkId}` }
      ]
    }),

    // The calls that would take the network back to a stored snapshot. A
    // mutation for the same reason as the pre-stage plan: every click reads
    // the live network afresh and nothing is cached under a key.
    buildNetworkStandardRestorePlan: builder.mutation<
      NetworkStandardRestorePlanResponse,
      { customerId: string; networkId: string; fileId: string }
    >({
      query: ({ customerId, ...input }) => ({
        url: `/customers/${customerId}/network-standard/restore-plan`,
        method: 'POST',
        body: input
      })
    }),

    // Recycle-bins a stored snapshot and refreshes the history.
    deleteNetworkStandardSnapshot: builder.mutation<
      NetworkStandardSnapshotDeleteResponse,
      { customerId: string; networkId: string; fileId: string }
    >({
      query: ({ customerId, ...input }) => ({
        url: `/customers/${customerId}/network-standard/snapshots/delete`,
        method: 'POST',
        body: input
      }),
      invalidatesTags: (_result, _error, { networkId }) => [
        { type: 'Files', id: `meraki_network-${networkId}` }
      ]
    }),

    // Reads the edited plan back from the dashboard, step by step. A
    // mutation because every click must read afresh; nothing is written.
    verifyNetworkStandardPlan: builder.mutation<
      NetworkStandardVerifyResponse,
      {
        customerId: string
        networkId: string
        aggregateSwitchSerial: string | null
        steps: EditableStep[]
      }
    >({
      query: ({ customerId, ...input }) => ({
        url: `/customers/${customerId}/network-standard/verify`,
        method: 'POST',
        body: input
      })
    }),

    // Sends the pre-stage to the dashboard. Takes the operator's inputs, not
    // the plan — the server rebuilds it — and refreshes the backup list,
    // because a push stores a snapshot before and after.
    pushNetworkStandardPlan: builder.mutation<
      NetworkStandardPushResponse,
      PrestageSiteInputs & {
        customerId: string
        networkId: string
        /** The operator's selection as edited; the server validates each one. */
        steps: EditableStep[]
        /** Set when the selection started from a stored snapshot's restore plan. */
        restoreFromFileId?: string
      }
    >({
      query: ({ customerId, ...input }) => ({
        url: `/customers/${customerId}/network-standard/push`,
        method: 'POST',
        body: { ...input, confirm: true }
      }),
      invalidatesTags: (_result, _error, { networkId }) => [
        { type: 'Files', id: `meraki_network-${networkId}` }
      ]
    }),

    // GET: network build detail — devices.network_build
    getNetworkBuild: builder.query<NetworkBuildResponse, string>({
      query: (customerId) => `/customers/${customerId}/network-build`,
      providesTags: (result, error, customerId) => [
        { type: 'NetworkBuild', id: customerId }
      ]
    }),

    // PATCH: stamps updated_by, which permanently exempts the row from the
    // hospitality sync — so it invalidates rather than optimistically patches.
    updateNetworkBuild: builder.mutation<
      NetworkBuildResponse,
      { customerId: string; changes: Partial<NetworkBuild> }
    >({
      query: ({ customerId, changes }) => ({
        url: `/customers/${customerId}/network-build`,
        method: 'PATCH',
        body: changes
      }),
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'NetworkBuild', id: customerId }
      ]
    }),

    // GET: AP zone assignments for the property
    // Sets zone_source='manual' on each AP, which stops the device syncs
    // recomputing it every five minutes. Invalidates `Devices` because zones are
    // read as part of the AP list (device-api `getAPsByCustomerId`) rather than
    // through an endpoint of their own — both inject into baseApi, so the one
    // tag registry is shared.
    updateAccessPointZones: builder.mutation<
      { customerId: string; applied: number; rejected: string[] },
      { customerId: string; updates: { serialNumber: string; zone: string }[] }
    >({
      query: ({ customerId, updates }) => ({
        url: `/customers/${customerId}/ap-zones`,
        method: 'PATCH',
        body: { updates }
      }),
      // NetworkBuild too: the Configuration panel shows AP counts by zone.
      invalidatesTags: (result, error, { customerId }) => [
        { type: 'Devices', id: customerId },
        { type: 'NetworkBuild', id: customerId }
      ]
    }),

    // GET /customers/:customerId/channels
    getChannelsByCustomerId: builder.query<ChannelType[], string>({
      query: (customerId) => `/customers/${customerId}/channels`,
      providesTags: (result, error, customerId) => [
        { type: 'Channels', id: customerId }
      ]
    }),

    // GET /customers/:customerId/smartboxes — cached chassis state (core +
    // sat inputs + blades + tuners) for the per-smartbox tabs on Channels
    getSmartboxesByCustomerId: builder.query<SmartboxSummary[], string>({
      query: (customerId) => `/customers/${customerId}/smartboxes`,
      providesTags: (result, error, customerId) => [
        { type: 'Channels', id: `${customerId}-smartboxes` }
      ]
    }),

    // GET /customers/:customerId/service-mute — internal-only summary of
    // whether local (FTG) channels are currently muted for non-payment.
    // Rides the ServiceMute tag so an accounting mute/unmute refreshes the
    // customer-overview banner without a reload.
    getCustomerServiceMute: builder.query<ServiceMuteSummary, string>({
      query: (customerId) => `/customers/${customerId}/service-mute`,
      providesTags: (_result, _error, customerId) => [
        { type: 'ServiceMute', id: customerId }
      ]
    }),

    // POST /customers/:customerId/smartboxes/:serial/jobs — start a device
    // job (store config / drift / restore / upgrade) via the sb2-mcp proxy
    startSmartboxJob: builder.mutation<
      SmartboxJobStart,
      {
        customerId: string
        serial: string
        action: SmartboxJobAction
        ip?: string | null
        params?: Record<string, unknown>
      }
    >({
      query: ({ customerId, serial, action, ip, params }) => ({
        url: `/customers/${customerId}/smartboxes/${serial}/jobs`,
        method: 'POST',
        body: { action, ip, params }
      })
    }),

    // GET /smartbox-jobs/:jobId — poll a running job (run + steps)
    getSmartboxJob: builder.query<SmartboxJobRun, number>({
      query: (jobId) => `/smartbox-jobs/${jobId}`
    }),

    // GET .../smartboxes/:serial/jobs?ip= — recent jobs for the device,
    // including runs initiated elsewhere (CLI, pilots, other users)
    getSmartboxJobs: builder.query<
      { jobs: SmartboxJobListItem[] },
      { customerId: string; serial: string; ip: string }
    >({
      query: ({ customerId, serial, ip }) =>
        `/customers/${customerId}/smartboxes/${serial}/jobs?ip=${encodeURIComponent(ip)}`
    }),

    // Surveys (customer / org / technician scoped)
    getCustomerSurveys: builder.query<SurveyListItem[], string>({
      query: (customerId) => `/customers/${customerId}/surveys`,
      providesTags: ['Surveys']
    }),
    getCustomerCsatScore: builder.query<CsatScoreData, string>({
      query: (customerId) => `/customers/${customerId}/csat-score`,
      providesTags: ['Surveys']
    }),
    getOrgSurveys: builder.query<SurveyListItem[], string>({
      query: (orgId) => `/organizations/${orgId}/surveys`,
      providesTags: ['Surveys']
    }),
    getTechnicianSurveys: builder.query<SurveyListItem[], string>({
      query: (techId) => `/technicians/${techId}/surveys`,
      providesTags: ['Surveys']
    }),
    getSurveyDetailById: builder.query<SurveyQuestion[], string>({
      query: (surveyId) => `/surveys/${surveyId}`,
      providesTags: ['Surveys']
    }),

    // Properties (Portal external surface — customer-adjacent at the data
    // layer; consumed by the Portal app views).
    getAccessibleProperties: builder.query<PropertyType[], string>({
      query: () => ({ url: '/properties' }),
      transformResponse: (res: { properties: PropertyType[] }) =>
        res.properties,
      providesTags: ['Properties']
    }),
    // The Portal properties list: the same rows plus status, size and
    // contract columns, from its own route so the many callers above keep
    // their light payload and the contract terms stay behind customers:read.
    getPortfolioProperties: builder.query<PortfolioPropertyType[], string>({
      query: () => ({ url: '/properties/portfolio' }),
      transformResponse: (res: { properties: PortfolioPropertyType[] }) =>
        res.properties,
      providesTags: ['Properties']
    }),
    getPropertyById: builder.query<PropertyType, string>({
      query: (propertyId) => ({ url: `/properties/${propertyId}` }),
      transformResponse: (res: { property: PropertyType }) => res.property,
      providesTags: (_result, _err, id) => [{ type: 'Properties', id }]
    }),

    // Slim property typeahead for assignment pickers.
    searchPropertiesTypeahead: builder.query<
      Array<{
        id: string
        name: string
        city: string | null
        state: string | null
        country: string | null
        customerId: string | null
      }>,
      { q: string; organizationId?: string }
    >({
      query: ({ q, organizationId }) => ({
        url: '/properties/search/typeahead',
        params: organizationId ? { q, organizationId } : { q }
      }),
      transformResponse: (res: {
        properties: Array<{
          id: string
          name: string
          city: string | null
          state: string | null
          country: string | null
          customerId: string | null
        }>
      }) => res.properties,
      providesTags: ['Properties']
    })
  })
})

export const {
  useSearchCustomersQuery,
  useLazySearchCustomersAdvancedQuery,
  useGetCustomerByIdQuery,
  useGetCustomerCountsQuery,
  useGetAllSpecialNotesByCustomerIdQuery,
  useCreateSpecialNoteForCustomerMutation,
  useGetAllSpecialNotesByTechnicianIdQuery,
  useCreateSpecialNoteForTechnicianMutation,
  useGetAllTechniciansQuery,
  useGetAllActiveTechniciansQuery,
  useGetTechnicianByIdQuery,
  useGetTechnicianSkillsQuery,
  useGetTechnicianEquipmentQuery,
  useGetSkillTypesQuery,
  useGetTechEquipmentTypesQuery,
  useCreateTechnicianSkillMutation,
  useUpdateTechnicianSkillMutation,
  useDeleteTechnicianSkillMutation,
  useCreateTechnicianEquipmentMutation,
  useUpdateTechnicianEquipmentMutation,
  useDeleteTechnicianEquipmentMutation,
  useGetEntityCustomersQuery,
  useAssignCustomersToEntityMutation,
  useResolvePropertyImportMutation,
  useRemoveCustomersFromEntityMutation,
  useGetEffectiveCustomersForUserQuery,
  useGetAccessibleCustomersQuery,
  useGetCustomerAccessQuery,
  useGetCustomerTimelineQuery,
  useGetFedExTrackingQuery,
  useGetCustomerNavAttributesQuery,
  useGetServicesSoldQuery,
  useGetSoldServiceOptionsQuery,
  useGetCustomerContractsQuery,
  useGetCustomerCountriesQuery,
  useGetCustomerLocalInfoQuery,
  useGetCustomerFeaturesQuery,
  useLazyGetCustomerFeaturesQuery,
  useGetCustomerManagedNetworkQuery,
  useGetCustomerConfigurationQuery,
  useGetNetworkStandardQuery,
  useBuildNetworkStandardPlanMutation,
  useLazyGetNetworkStandardLiveQuery,
  useSaveNetworkStandardSnapshotMutation,
  useGetNetworkStandardSnapshotsQuery,
  useDeleteNetworkStandardSnapshotMutation,
  useBuildNetworkStandardRestorePlanMutation,
  useVerifyNetworkStandardPlanMutation,
  usePushNetworkStandardPlanMutation,
  useGetNetworkBuildQuery,
  useUpdateNetworkBuildMutation,
  useUpdateAccessPointZonesMutation,
  useLazyGetCustomerManagedNetworkQuery,
  useGetChannelsByCustomerIdQuery,
  useGetSmartboxesByCustomerIdQuery,
  useGetCustomerServiceMuteQuery,
  useStartSmartboxJobMutation,
  useGetSmartboxJobQuery,
  useGetSmartboxJobsQuery,
  useGetCustomerSurveysQuery,
  useGetCustomerCsatScoreQuery,
  useGetOrgSurveysQuery,
  useGetTechnicianSurveysQuery,
  useGetSurveyDetailByIdQuery,
  useGetAccessiblePropertiesQuery,
  useGetPortfolioPropertiesQuery,
  useGetPropertyByIdQuery,
  useSearchCustomersTypeaheadQuery,
  useSearchPropertiesTypeaheadQuery,
  useLookupCustomersByIdsMutation
} = customerApi
